import React, { useEffect } from "react";

/**
 * Universal embed for YouTube + TikTok videos.
 *
 * YouTube: standard nocookie iframe (avoids GDPR/CMP conflicts with the consent
 * banner — no tracking cookie set until the visitor actually presses play).
 * TikTok: TikTok's official blockquote + async script.
 */
export function VideoEmbed({ video, autoplay = false, className = "" }) {
    // Injected once for TikTok — always runs (hooks must be unconditional).
    useEffect(() => {
        if (!video || video.source !== "tiktok") return;
        if (typeof document === "undefined") return;
        if (document.getElementById("tiktok-embed-script")) {
            if (window.tiktokEmbed) window.tiktokEmbed.lib.render();
            return;
        }
        const s = document.createElement("script");
        s.id = "tiktok-embed-script";
        s.async = true;
        s.src = "https://www.tiktok.com/embed.js";
        document.body.appendChild(s);
    }, [video]);

    if (!video) return null;

    if (video.source === "youtube") {
        const src = `https://www.youtube-nocookie.com/embed/${video.external_id}?rel=0&modestbranding=1${autoplay ? "&autoplay=1" : ""}`;
        return (
            <div className={`relative w-full aspect-video overflow-hidden rounded-2xl bg-black ${className}`}>
                <iframe
                    src={src}
                    title={video.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="absolute inset-0 w-full h-full border-0"
                    data-testid={`youtube-embed-${video.external_id}`}
                />
            </div>
        );
    }

    if (video.source === "tiktok") {
        return (
            <div className={`w-full flex justify-center ${className}`}>
                <blockquote
                    className="tiktok-embed"
                    cite={video.external_url}
                    data-video-id={video.external_id}
                    style={{ maxWidth: 605, minWidth: 288 }}
                    data-testid={`tiktok-embed-${video.external_id}`}
                >
                    <section>
                        <a href={video.external_url} target="_blank" rel="noopener noreferrer">
                            {video.title}
                        </a>
                    </section>
                </blockquote>
            </div>
        );
    }

    return null;
}
